import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useLoginQuery } from "src/authentication/data-access/useLoginQuery";
import { FormContainer, TextFieldElement, useForm } from "react-hook-form-mui";
import * as yup from "yup";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import { ILoginUser } from "src/authentication/data-access/gateway";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import InfoMessage from "src/shared/components/InfoMessage";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import { useResendValidationEmail } from "src/authentication/data-access/useResendValidationEmail";
import LoginIcon from "@mui/icons-material/Login";

export default function loginUser({
  type,
}: {
  type: "user" | "nonGemi" | "chamber" | "region";
}) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [needToValidate, setNeedToValidate] = useState(false);
  const [mailValidationToken, setMailValidationToken] = useState(undefined);
  const navigate = useNavigate();

  const validationSchema = yup.object().shape({
    username: yup
      .string()
      .required(t("Το Όνομα Χρήστη είναι υποχρεωτικό"))
      .min(
        type == "user" ? 7 : 0,
        t("Το Όνομα Χρήστη πρέπει να είναι τουλάχιστον 7 χαρακτήρες"),
      ),
    password: yup
      .string()
      .required(t("Ο Κωδικός Πρόσβασης είναι υποχρεωτικός"))
      .min(
        type == "user" ? 7 : 0,
        t("Ο Κωδικός Πρόσβασης πρέπει να είναι τουλάχιστον 7 χαρακτήρες"),
      ),
  });

  const formContext = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { mutate: loginMutate, isLoading: isLoadingLogin } = useLoginQuery(
    setNeedToValidate,
    setMailValidationToken,
  );

  const {
    mutate: resendValidationEmailMutate,
    isLoading: isLoadingResendValidationEmail,
  } = useResendValidationEmail();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "80%",
        justifyContent: "flex-end",
      }}
    >
      <Typography variant={"h6"}>{t("Καλώς ήρθατε")}</Typography>
      <Typography variant={"body1"}>
        {t("Εισάγετε τα στοιχεία σας για πρόσβαση στις υπηρεσίες.")}
      </Typography>
      <FormContainer
        formContext={formContext}
        onSuccess={(data: ILoginUser) => {
          loginMutate({ userData: data, type });
        }}
        onError={(formError) => {
          Object.entries(formError).forEach(([key, value]) => {
            value?.message
              ? enqueueSnackbar(value?.message, { variant: "error" })
              : null;
          });
        }}
      >
        <TextFieldElement
          fullWidth
          sx={{
            "& :-webkit-autofill": {
              transitionDelay: "9999s",
            },
          }}
          required
          autoComplete="username"
          variant="standard"
          name="username"
          label={t("Όνομα Χρήστη")}
        />
        <TextFieldElement
          fullWidth
          sx={{
            mt: 5,
            "& :-webkit-autofill": {
              transitionDelay: "9999s",
            },
            "& .MuiFormLabel-root": {
              fontSize: "1.1rem",
            },
          }}
          required
          variant="standard"
          type={showPassword ? "text" : "password"}
          name="password"
          label={t("Κωδικός Πρόσβασης")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {(type === "user" || type === "nonGemi") && (
          <Box sx={{ mt: 3, fontSize: ".8rem" }}>
            <Button
              variant={"outlined"}
              onClick={() => navigate("/welcome/reminderForm")}
            >
              {t("Ξέχασα τον κωδικό μου")}
            </Button>
          </Box>
        )}
        {needToValidate && (
          <Box sx={{ mt: 3 }}>
            <InfoMessage
              message={
                <Typography>
                  {t("Ελέγξτε ξανά το mail σας ή πατήστε")}{" "}
                  <LoadingButton
                    sx={{
                      minWidth: "auto",
                      padding: 0,
                      margin: 0,
                      verticalAlign: "baseline",
                      fontSize: "inherit",
                      background: "none",
                      boxShadow: "none",
                      color: "#1976d2",
                      textTransform: "none",
                      "&:hover": {
                        background: "none",
                        textDecoration: "underline",
                      },
                    }}
                    loading={isLoadingResendValidationEmail}
                    onClick={() => {
                      mailValidationToken &&
                        resendValidationEmailMutate(mailValidationToken);
                    }}
                  >
                    {t("εδώ")}
                  </LoadingButton>{" "}
                  {t("για εκ νέου αποστολή του email ενεργοποίησης")}
                </Typography>
              }
              variant={MessageVariant.Info}
            />
          </Box>
        )}
        <LoadingButton
          sx={{
            pt: 2,
            pb: 2,
            mt: 3,
            height: "3rem",
            borderRadius: "2rem",
            background: "#003476",
            "&:hover": {
              backgroundColor: "#00449c",
            },
          }}
          fullWidth
          data-testid="loginButton"
          variant="contained"
          loading={isLoadingLogin}
          type="submit"
          startIcon={<LoginIcon />}
        >
          {t("Είσοδος")}
        </LoadingButton>
      </FormContainer>
    </Box>
  );
}
